import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Container,
  Grid,
  Card,
  CardContent,
  Paper,
  Chip,
  Avatar,
  LinearProgress,
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  People,
  School,
  Person,
  TrendingUp,
  Assignment,
  Assessment,
  Payment,
  Home,
  Class,
  Subject,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';

const Dashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    total_users: 0,
    total_students: 0,
    total_teachers: 0,
    total_classes: 0,
    total_subjects: 0,
    attendance_rate: 0,
    exam_pass_rate: 0,
    pending_fees_count: 0
  });
  const [loading, setLoading] = useState(true);
  const [userProfile, setUserProfile] = useState(null);
  const [schoolInfo, setSchoolInfo] = useState({
    school_name: 'EMIS School',
    school_logo: null
  });

  useEffect(() => {
    fetchDashboardStats();
    fetchUserProfile();
    fetchSchoolInfo();
  }, []);

  const fetchUserProfile = async () => {
    try {
      const response = await axios.get('profile');
      if (response.data.success) {
        setUserProfile(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching user profile:', error);
    }
  };

  const fetchDashboardStats = async () => {
    try {
      setLoading(true);
      const response = await axios.get('settings/dashboard-stats');
      if (response.data.success) {
        setStats(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching dashboard stats:', error);
      // Keep default values on error
    } finally {
      setLoading(false);
    }
  };

  const fetchSchoolInfo = async () => {
    try {
      const response = await axios.get('/api/settings/school-info');
      if (response.data.success) {
        setSchoolInfo({
          school_name: response.data.data.school_name || 'EMIS School',
          school_logo: response.data.data.school_logo
        });
      }
    } catch (error) {
      console.error('Error fetching school info:', error);
      // Keep default values on error
      setSchoolInfo({
        school_name: 'EMIS School',
        school_logo: null
      });
    }
  };

  const getRoleColor = (role) => {
    switch (role) {
      case 'admin': return 'error';
      case 'teacher': return 'primary';
      case 'student': return 'success';
      case 'parent': return 'warning';
      default: return 'default';
    }
  };

  const dashboardCards = [
    {
      title: 'Total Students',
      value: loading ? '...' : stats.total_students || 0,
      icon: <Person />,
      color: 'primary.main',
      description: 'Active students enrolled'
    },
    {
      title: 'Total Teachers',
      value: loading ? '...' : stats.total_teachers || 0,
      icon: <School />,
      color: 'secondary.main',
      description: 'Teaching staff members'
    },
    {
      title: 'Total Classes',
      value: loading ? '...' : stats.total_classes || 0,
      icon: <Class />,
      color: 'success.main',
      description: 'Active class sections'
    },
    {
      title: 'Total Subjects',
      value: loading ? '...' : stats.total_subjects || 0,
      icon: <Subject />,
      color: 'warning.main',
      description: 'Subjects being taught'
    },
    {
      title: 'Attendance Rate',
      value: loading ? '...' : `${parseFloat(stats.attendance_rate || 0).toFixed(2)}%`,
      icon: <Assignment />,
      color: 'info.main',
      description: 'Average attendance rate (last 30 days)'
    },
    {
      title: 'Exam Pass Rate',
      value: loading ? '...' : `${parseFloat(stats.exam_pass_rate || 0).toFixed(2)}%`,
      icon: <Assessment />,
      color: 'success.main',
      description: 'Pass rate (recent exams)'
    },
    {
      title: 'Pending Fees',
      value: loading ? '...' : stats.pending_fees_count || 0,
      icon: <Payment />,
      color: 'error.main',
      description: 'Students with pending fees'
    },
    {
      title: 'Total Users',
      value: loading ? '...' : stats.total_users || 0,
      icon: <People />,
      color: 'primary.main',
      description: 'System users'
    }
  ];

  const quickActions = [
    {
      title: 'User Management',
      icon: <People sx={{ fontSize: 32, mb: 1 }} />,
      path: '/admin/users',
      color: 'primary.main',
      adminOnly: true
    },
    {
      title: 'Students',
      icon: <Person sx={{ fontSize: 32, mb: 1 }} />,
      path: '/admin/students',
      color: 'secondary.main',
      adminOnly: false
    },
    {
      title: 'Teachers',
      icon: <School sx={{ fontSize: 32, mb: 1 }} />,
      path: '/admin/teachers',
      color: 'secondary.main',
      adminOnly: true
    },
    {
      title: 'Classes',
      icon: <Class sx={{ fontSize: 32, mb: 1 }} />,
      path: '/admin/classes',
      color: 'info.main',
      adminOnly: false
    },
    {
      title: 'Subjects',
      icon: <Subject sx={{ fontSize: 32, mb: 1 }} />,
      path: '/admin/subjects',
      color: 'warning.main',
      adminOnly: false
    },
    {
      title: 'Attendance',
      icon: <Assignment sx={{ fontSize: 32, mb: 1 }} />,
      path: '/admin/attendance',
      color: 'success.main',
      adminOnly: false
    },
    {
      title: 'Exams',
      icon: <Assessment sx={{ fontSize: 32, mb: 1 }} />,
      path: '/admin/exams',
      color: 'warning.main',
      adminOnly: false
    },
    {
      title: 'Fees',
      icon: <Payment sx={{ fontSize: 32, mb: 1 }} />,
      path: '/admin/fees',
      color: 'error.main',
      adminOnly: true
    }
  ];

  const handleQuickActionClick = (path, event) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    console.log('Navigating to:', path);
    navigate(path);
  };

  return (
    <Container maxWidth="xl">
      {/* Welcome Section */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <Paper 
          sx={{ 
            p: 4, 
            mb: 4, 
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', 
            color: 'white',
            borderRadius: 3,
            boxShadow: '0 8px 32px rgba(102, 126, 234, 0.3)'
          }}
        >
          {/* School Header */}
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, pb: 2, borderBottom: '1px solid rgba(255,255,255,0.2)' }}>
            {schoolInfo.school_logo ? (
              <img
                src={`http://localhost:3000${schoolInfo.school_logo}`}
                alt="School Logo"
                style={{
                  height: 60,
                  width: 60,
                  objectFit: 'contain',
                  marginRight: 16,
                  borderRadius: 8,
                  border: '2px solid rgba(255,255,255,0.3)'
                }}
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'inline-block';
                }}
              />
            ) : null}
            <School 
              sx={{ 
                mr: 2, 
                fontSize: 60,
                display: schoolInfo.school_logo ? 'none' : 'inline-block',
                opacity: 0.8
              }} 
            />
            <Box>
              <Typography variant="h4" sx={{ fontWeight: 'bold', opacity: 0.9 }}>
                {schoolInfo.school_name}
              </Typography>
              <Typography variant="body1" sx={{ opacity: 0.7 }}>
                {user?.role === 'admin' ? 'Administration Portal' : 'Teacher Portal'}
              </Typography>
            </Box>
          </Box>

          {/* User Profile Section */}
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Avatar 
              sx={{ 
                width: 80, 
                height: 80, 
                mr: 3, 
                bgcolor: userProfile?.profile_picture ? 'transparent' : 'rgba(255,255,255,0.2)',
                fontSize: '2rem',
                fontWeight: 'bold',
                border: '3px solid rgba(255,255,255,0.3)'
              }}
              src={userProfile?.profile_picture ? `http://localhost:3000/uploads/profiles/${userProfile.profile_picture}` : undefined}
            >
              {!userProfile?.profile_picture && (
                userProfile?.first_name && userProfile?.last_name 
                  ? `${userProfile.first_name.charAt(0)}${userProfile.last_name.charAt(0)}`.toUpperCase()
                  : (userProfile?.first_name?.charAt(0) || user?.username?.charAt(0) || 'U').toUpperCase()
              )}
            </Avatar>
            <Box>
              <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 1 }}>
                Welcome back, {userProfile?.first_name || user?.username}!
              </Typography>
              <Typography variant="h6" sx={{ opacity: 0.9, mb: 1 }}>
                {userProfile?.first_name && userProfile?.last_name 
                  ? `${userProfile.first_name} ${userProfile.last_name}` 
                  : (user?.role === 'admin' ? 'Administrator' : 'Teacher')}
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                <Chip
                  label={user?.role?.toUpperCase()}
                  color={getRoleColor(user?.role)}
                  sx={{ 
                    fontWeight: 'bold',
                    color: 'white',
                    bgcolor: 'rgba(255,255,255,0.2)'
                  }}
                />
                {userProfile?.email && (
                  <Chip 
                    label={userProfile.email} 
                    sx={{ bgcolor: 'rgba(255,255,255,0.1)', color: 'white', fontWeight: 'bold' }}
                  />
                )}
              </Box>
            </Box>
          </Box>
        </Paper>
      </motion.div>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {dashboardCards.map((card, index) => (
          <Grid item xs={12} sm={6} md={3} key={card.title}>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              whileHover={{ scale: 1.02 }}
            >
              <Card 
                sx={{ 
                  height: '100%',
                  borderRadius: 3,
                  boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    boxShadow: '0 8px 32px rgba(0,0,0,0.15)',
                  }
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Avatar sx={{ bgcolor: card.color, mr: 2, width: 48, height: 48 }}>
                      {card.icon}
                    </Avatar>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                        {card.value}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {card.title}
                      </Typography>
                    </Box>
                  </Box>
                  <Typography variant="caption" color="text.secondary">
                    {card.description}
                  </Typography>
                  {card.title.includes('Rate') && (
                    <LinearProgress 
                      variant="determinate" 
                      value={parseInt(card.value)} 
                      sx={{ mt: 1, borderRadius: 1 }}
                    />
                  )}
                </CardContent>
              </Card>
            </motion.div>
          </Grid>
        ))}
      </Grid>

      {/* Quick Actions and Recent Activity */}
      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.8 }}
          >
            <Card sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.1)' }}>
              <CardContent sx={{ p: 3 }}>
                <Typography variant="h6" sx={{ mb: 3, fontWeight: 'bold', display: 'flex', alignItems: 'center' }}>
                  <TrendingUp sx={{ mr: 1, color: 'primary.main' }} />
                  Quick Actions
                </Typography>
                <Grid container spacing={2}>
                  {quickActions
                    .filter(action => !action.adminOnly || user?.role === 'admin')
                    .map((action, index) => (
                      <Grid item xs={6} sm={4} key={action.title}>
                        <motion.div
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.98 }}
                        >
                          <Paper
                            onClick={(e) => handleQuickActionClick(action.path, e)}
                            sx={{ 
                              p: 2, 
                              textAlign: 'center', 
                              cursor: 'pointer', 
                              borderRadius: 2,
                              transition: 'all 0.3s ease',
                              '&:hover': { 
                                bgcolor: action.color,
                                color: 'white',
                                transform: 'translateY(-2px)',
                                boxShadow: '0 4px 20px rgba(0,0,0,0.2)'
                              } 
                            }}
                          >
                            {action.icon}
                            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                              {action.title}
                            </Typography>
                          </Paper>
                        </motion.div>
                      </Grid>
                    ))
                  }
                </Grid>
              </CardContent>
            </Card>
          </motion.div>
        </Grid>

        <Grid item xs={12} md={4}>
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 1 }}
          >
            <Card sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.1)' }}>
              <CardContent sx={{ p: 3 }}>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
                  System Status
                </Typography>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Database Connection
                  </Typography>
                  <Chip label="Active" color="success" size="small" />
                </Box>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Server Status
                  </Typography>
                  <Chip label="Running" color="success" size="small" />
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Last Backup
                  </Typography>
                  <Typography variant="body2">
                    Today, 2:00 AM
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </motion.div>
        </Grid>
      </Grid>
    </Container>
  );
};

export default Dashboard;
